// eTAMS — My expense reports (PDF page 5).
// The claim form itself now lives on its own page (/etams/claims/new, PDF p6);
// every existing capability is kept: drafts (edit / submit), expense items
// view, attachments, payment status, search + date filter + pagination, and
// approvers still see their team's claims (Team tab).
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { notify, confirm } from '../../utils/notify';
import { expenseAPI, openExpenseAttachment } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useListControls } from '../../components/ListControls';
import { claimActorName, withActor, paymentPill } from '../../utils/expenseStatus';
import {
  PageShell, PageHead, Stat, Card, Badge, Note, Seg, Modal, Kebab, Empty, Loading, Pager, VFlow, FileChip,
  money, pad2, fmtRange, fmtDate, fmtDateTime, downloadCSV,
} from '../../components/ui';
import { claimStatus, attachmentsOf, netOf } from '../../utils/etams';

function ExpenseClaimSubmission() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [scope, setScope] = useState('mine');
  const [view, setView] = useState(null);
  const [submittingDraftId, setSubmittingDraftId] = useState(null);

  const load = () => {
    setLoading(true);
    expenseAPI.listClaims({ limit: 300 }).then((c) => setClaims(c.claims || [])).catch((e) => notify.error(e.message || 'Failed to load')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const isMine = (c) => String(c.requestedBy?._id || c.requestedBy) === String(user?.id);
  const mine = claims.filter(isMine);
  const hasTeam = claims.some((c) => !isMine(c));
  const base = scope === 'mine' ? mine : claims.filter((c) => !isMine(c));
  const TABS = {
    all: () => true,
    drafts: (c) => c.status === 'DRAFT',
    review: (c) => ['SUBMITTED', 'MANAGER_APPROVED'].includes(c.status),
    approved: (c) => c.status === 'FINANCE_APPROVED',
    paid: (c) => c.status === 'SETTLED',
    rejected: (c) => c.status === 'REJECTED',
  };
  const filtered = useMemo(() => base.filter(TABS[tab]), [base, tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const lc = useListControls(filtered, {
    searchFields: [(r) => r.claimId, (r) => r.reportTitle, (r) => r.employeeName || r.requestedBy?.userName, 'purposeOfTravel', 'travelLocation', 'projectCode'],
    dateField: 'createdAt', pageSize: 10,
  });

  const live = mine.filter((c) => c.status !== 'REJECTED');
  const stats = {
    employeeClaims: live.reduce((s, c) => s + (c.totalExpenses || 0), 0),
    pending: mine.filter(TABS.review).length,
    approvedPayable: mine.filter(TABS.approved).reduce((s, c) => s + netOf(c), 0),
    companyPaid: live.reduce((s, c) => s + (c.companyPaidTotal || 0), 0),
  };

  const submitDraft = async (r) => {
    const res = await confirm.action('Submit for approval?', 'This draft will be sent for Manager approval and can no longer be edited freely.', 'Yes, Submit', '#1479bd');
    if (!res.isConfirmed) return;
    setSubmittingDraftId(r._id);
    try { await expenseAPI.submitClaim(r._id); notify.success('Expense claim submitted for approval!'); load(); } catch (e) { notify.error(e.message || 'Could not submit — please edit the draft to fill in missing fields'); } finally { setSubmittingDraftId(null); }
  };

  const exportCsv = () => downloadCSV('expense-reports.csv', lc.filtered, [
    { label: 'Claim ID', value: 'claimId' }, { label: 'Report', value: (r) => r.reportTitle || r.purposeOfTravel }, { label: 'Employee', value: 'employeeName' },
    { label: 'Project', value: 'projectCode' }, { label: 'From', value: (r) => fmtDate(r.travelStartDate) }, { label: 'To', value: (r) => fmtDate(r.travelEndDate) },
    { label: 'Employee claim', value: 'totalExpenses' }, { label: 'Company paid', value: 'companyPaidTotal' }, { label: 'Advance', value: 'advanceReceived' },
    { label: 'Net amount', value: (r) => netOf(r) }, { label: 'Status', value: (r) => claimStatus(r).label },
  ]);

  return (
    <PageShell section="Travel & Expenses">
      <PageHead title="My expense reports"
        actions={<Link to="/etams/claims/new" className="tse-btn lg"><i className="bi bi-plus-circle" />Create expense claim</Link>} />

      <div className="tse-grid g-4 mb-18">
        <Stat icon="bi-file-earmark-text" tone="blue" label="Employee claims" value={money(stats.employeeClaims)} loading={loading} />
        <Stat icon="bi-clock" tone="amber" label="Pending review" value={pad2(stats.pending)} loading={loading} onClick={() => setTab('review')} />
        <Stat icon="bi-check-circle" tone="green" label="Approved payable" value={money(stats.approvedPayable)} loading={loading} onClick={() => setTab('approved')} />
        <Stat icon="bi-database" tone="gray" label="Company-paid costs" value={money(stats.companyPaid)} loading={loading} />
      </div>

      <Card className="mb-18" bodyClass="tight">
        <Seg value={tab} onChange={setTab} tabs={[
          { key: 'all', label: 'All reports' }, { key: 'drafts', label: 'Drafts' }, { key: 'review', label: 'In review' },
          { key: 'approved', label: 'Approved' }, { key: 'paid', label: 'Paid' },
          ...(base.some(TABS.rejected) ? [{ key: 'rejected', label: 'Rejected' }] : []),
        ]} />
        <div className="tse-toolbar" style={{ padding: '10px 0 4px' }}>
          <div className="tse-search"><i className="bi bi-search" /><input placeholder="Search by report, claim ID, project…" value={lc.search} onChange={(e) => lc.setSearch(e.target.value)} /></div>
          <div className="tse-row tse-small tse-muted" style={{ gap: 8 }}>
            Submitted <input type="date" className="tse-input" style={{ width: 150, height: 38 }} value={lc.dateFrom} onChange={(e) => lc.setDateFrom(e.target.value)} />
            to <input type="date" className="tse-input" style={{ width: 150, height: 38 }} value={lc.dateTo} onChange={(e) => lc.setDateTo(e.target.value)} />
          </div>
          <div className="tse-spacer" />
          {hasTeam && <div style={{ width: 250 }}><Seg value={scope} onChange={setScope} tabs={[{ key: 'mine', label: 'My reports' }, { key: 'team', label: 'Team reports' }]} /></div>}
          <button type="button" className="tse-btn outline sm" onClick={exportCsv}><i className="bi bi-box-arrow-up" />Export</button>
        </div>
        {loading ? <Loading /> : lc.paginated.length === 0 ? <Empty icon="bi-file-earmark-text" title="No expense reports" action={<Link to="/etams/claims/new" className="tse-btn sm">Create expense claim</Link>} /> : (
          <div className="tse-card" style={{ boxShadow: 'none' }}>
            <div className="tse-table-wrap">
              <table className="tse-table">
                <thead><tr><th>Report</th><th>Travel period</th><th>Employee claim</th><th>Advance</th><th>Net amount</th><th>Status</th><th style={{ width: 36 }} /></tr></thead>
                <tbody>
                  {lc.paginated.map((r) => {
                    const st = claimStatus(r);
                    const own = isMine(r) || user?.role === 'admin';
                    const pay = paymentPill(r);
                    return (
                      <tr key={r._id} className="clickable" onClick={() => setView(r)}>
                        <td>
                          <div className="tse-strong" style={{ fontSize: '0.902rem' }}>{r.reportTitle || r.purposeOfTravel}</div>
                          <div className="tse-muted">{r.claimId}{scope !== 'mine' ? ` · ${r.employeeName}` : ''}</div>
                        </td>
                        <td>{fmtRange(r.travelStartDate, r.travelEndDate)}</td>
                        <td>{money(r.totalExpenses)}</td>
                        <td>{money(r.advanceReceived)}</td>
                        <td style={{ color: netOf(r) < 0 ? '#c0392b' : undefined }}>{money(Math.abs(netOf(r)))}{netOf(r) < 0 ? ' (recoverable)' : ''}</td>
                        <td><Badge tone={st.tone} icon={st.icon} lg title={withActor(st.label, claimActorName(r))}>{st.label}</Badge>{pay && r.status === 'FINANCE_APPROVED' && <div className="tse-small tse-muted" style={{ marginTop: 3 }}>{pay.label}</div>}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <Kebab items={[
                            { label: 'View details', icon: 'bi-eye', onClick: () => setView(r) },
                            { label: 'Edit draft', icon: 'bi-pencil', hidden: !(r.status === 'DRAFT' && own), onClick: () => navigate(`/etams/claims/new?id=${r._id}`) },
                            { label: submittingDraftId === r._id ? 'Submitting…' : 'Submit for approval', icon: 'bi-send', hidden: !(r.status === 'DRAFT' && own), disabled: submittingDraftId === r._id, onClick: () => submitDraft(r) },
                            { label: 'Add receipts', icon: 'bi-paperclip', hidden: !own || ['SETTLED', 'REJECTED'].includes(r.status), onClick: () => navigate('/etams/receipts') },
                          ]} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pager page={lc.page} pages={lc.totalPages} total={lc.totalCount} pageSize={lc.pageSize} setPage={lc.setPage} label="reports" />
          </div>
        )}
      </Card>

      <Note tone="info" big title="Company/client-paid expenses are excluded from employee reimbursement.">
        <span className="tse-muted">Ref: TSE-HR-TEC-001 · Proposed Rev. 00.</span>
      </Note>

      <Modal open={!!view} onClose={() => setView(null)} size="xl" title={view ? `${view.reportTitle || view.purposeOfTravel}` : ''} sub={view ? `${view.claimId} · ${fmtRange(view.travelStartDate, view.travelEndDate)}` : ''}
        footer={view && view.status === 'DRAFT' && (isMine(view) || user?.role === 'admin') && (
          <>
            <button type="button" className="tse-btn outline" onClick={() => navigate(`/etams/claims/new?id=${view._id}`)}>Edit draft</button>
            <button type="button" className="tse-btn" onClick={() => { setView(null); submitDraft(view); }}>Submit for approval</button>
          </>
        )}>
        {view && (() => {
          const st = claimStatus(view);
          const returned = (view.approvalHistory || []).filter((h) => h.action === 'RETURNED').slice(-1)[0];
          return (
            <div className="tse-stack" style={{ gap: 8 }}>
              <div className="tse-row"><Badge tone={st.tone} icon={st.icon} lg>{withActor(st.label, claimActorName(view))}</Badge>{view.utrReference && <Badge tone="blue">UTR {view.utrReference}</Badge>}</div>
              {view.status === 'DRAFT' && returned && <Note tone="warn" title="Returned for correction">{returned.remarks}</Note>}
              <div className="tse-form-grid c4">
                {[['Employee', view.employeeName], ['Project', view.projectCode || '—'], ['Sanction', view.travelRequestNo || '—'], ['Location', view.travelLocation || '—'],
                  ['Tour report', view.tourReportRef || '—'], ['Shared expenses', view.sharedExpenses || '—'], ['Submitted', fmtDate(view.createdAt)], ['Settled', view.settledAt ? `${fmtDate(view.settledAt)} · ${view.settlementMode || ''}` : '—']]
                  .map(([k, v]) => <div key={k}><div className="tse-muted tse-small">{k}</div><div className="tse-strong">{v}</div></div>)}
              </div>
              <div className="tse-card" style={{ boxShadow: 'none' }}>
                <div className="tse-table-wrap">
                  <table className="tse-table compact">
                    <thead><tr><th>#</th><th>Category</th><th>Date</th><th>Description / bill ref.</th><th>Paid by</th><th className="num">Base</th><th className="num">GST</th><th className="num">Total</th></tr></thead>
                    <tbody>
                      {(view.expenses || []).map((e, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td><td>{e.expenseType}</td><td>{fmtDate(e.date)}</td><td>{[e.description, e.billReference].filter(Boolean).join(' · ') || '—'}</td>
                          <td>{e.paidBy || 'Employee'}</td><td className="num">{money(e.amount)}</td><td className="num">{money(e.gst)}</td><td className="num strong">{money(e.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="tse-split half">
                <div>
                  {attachmentsOf(view).length > 0 ? (
                    <>
                      <div className="tse-label" style={{ marginBottom: 8 }}>Supporting documents</div>
                      <div className="tse-stack" style={{ gap: 8 }}>{attachmentsOf(view).map((a, i) => <FileChip key={a.s3Key || i} file={a} onOpen={() => openExpenseAttachment(a).catch(() => notify.error('Could not open attachment'))} />)}</div>
                    </>
                  ) : <div className="tse-muted tse-small">No supporting documents attached.</div>}
                  {(view.approvalHistory || []).length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <div className="tse-label" style={{ marginBottom: 8 }}>Approval trail</div>
                      <VFlow items={view.approvalHistory.map((h) => ({ state: { APPROVED: 'done', SETTLED: 'done', REJECTED: 'rejected', RETURNED: 'returned' }[h.action], title: `${h.action.charAt(0)}${h.action.slice(1).toLowerCase()} · ${h.role}`, sub: h.remarks, meta: <>{h.approverId?.userName || ''}<br />{fmtDateTime(h.actionAt)}</> }))} />
                    </div>
                  )}
                </div>
                <div>
                  <div className="tse-kv-row"><span className="k">Employee expenses</span><span className="v">{money(view.totalExpenses)}</span></div>
                  <div className="tse-kv-row"><span className="k">Company paid</span><span className="v">{money(view.companyPaidTotal)}</span></div>
                  <div className="tse-kv-row"><span className="k">Advance (deduct)</span><span className="v">{money(view.advanceReceived)}</span></div>
                  <div className="tse-kv-row total"><span className="k">{netOf(view) < 0 ? 'Recoverable from employee' : view.status === 'SETTLED' ? 'Net paid' : 'Net reimbursement'}</span><span className="v">{money(Math.abs(view.status === 'SETTLED' ? (view.settlementAmount ?? netOf(view)) : netOf(view)))}</span></div>
                  {view.amountModified && view.approvedAmount != null && <div className="tse-small tse-muted" style={{ marginTop: 8 }}>Amount revised during approval{view.amountModifiedBy?.userName ? ` by ${view.amountModifiedBy.userName}` : ''}.</div>}
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>
    </PageShell>
  );
}
export default ExpenseClaimSubmission;
